import React from "react";
import App, { AppProps, Container, DefaultAppIProps, NextAppContext } from "next/app";

import Router from "next/router";
import { registerIcons } from "@comparaonline/ui-offer-icons";
import { config, library } from "@fortawesome/fontawesome-svg-core";
import "../src/css/app.css";
import { trackPageView } from "../src/lib/analytics";
import { withApolloClient } from "../src/lib/apollo";
import { AppContextProvider } from "../src/lib/appContext";
import { AttributionProvider } from "../src/lib/attributionContext";
import { MaterialUIPageContext, withMaterialUIProvider } from "../src/lib/materialUI";
import { withIntlProvider } from "../src/lib/reactIntl";
import { RouterContextProvider } from "../src/lib/routerContext";
import { ExtendedRequest } from "../src/server/types";
import {
  getThirdPartyScriptsInstallerCallback,
  safeInstallThirdPartyScripts,
  thirdPartyScriptInstallerRunner
} from "../src/lib/thirdPartyScripts";
import { FirebaseProvider } from "./FirebaseProvider";
import { VwoSmartCode } from "../src/components/VwoSmartCode";
import { getConfig } from "../config/app";

interface MyAppProps extends DefaultAppIProps, AppProps, ExtendedRequest {
  pageContext: MaterialUIPageContext;
}

registerIcons(library);
config.autoAddCss = false;

const vwoAccountID = getConfig("vwo.accountID");

class MyApp extends App<MyAppProps> {
  public static async getInitialProps({ Component, ctx }: NextAppContext) {
    const pageProps = Component.getInitialProps ? await Component.getInitialProps(ctx) : {};

    const {
      lib,
      country,
      countryCode,
      isMobile,
      businessUnitConfig,
      businessUnitSlug,
      hostname,
      menu: { footer, topbar },
      language,
      locale,
      fullUrl
    } = ctx.req || window.__NEXT_DATA__.props;

    return {
      businessUnitConfig,
      businessUnitSlug,
      country,
      countryCode,
      host: hostname,
      isMobile,
      language,
      lib,
      locale,
      fullUrl,
      menu: { footer, topbar },
      ...pageProps
    };
  }

  public componentDidMount() {
    const { lib = {}, countryCode } = this.props;
    const { googleAnalyticsId = "", googleTagManagerId = "" } = lib;

    if (googleAnalyticsId) {
      Router.onRouteChangeComplete = trackPageView;
    }

    // register third party scripts
    const tasks = getThirdPartyScriptsInstallerCallback();
    const thirdPartyIds = { googleAnalyticsId, googleTagManagerId };
    safeInstallThirdPartyScripts(thirdPartyIds);
    thirdPartyScriptInstallerRunner(tasks, thirdPartyIds).runCurrent();
    const userAccountLibConfig = getConfig(`userAccountLib.${countryCode}`);
    if (userAccountLibConfig && userAccountLibConfig.enabled) {
      import("@comparaonlineprivate/user-account-lib")
        .then(mod => mod.initialize)
        .then(initialize => {
          initialize(userAccountLibConfig);
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.error("Error loading the user account library:", error);
        });
    }
  }

  public render() {
    const {
      Component,
      businessUnitConfig,
      businessUnitSlug,
      country,
      countryCode,
      host,
      isMobile,
      language,
      locale,
      fullUrl,
      menu: { footer, topbar },
      ...pageProps
    } = this.props;

    return (
      <Container>
        <VwoSmartCode accountID={vwoAccountID} />
        <RouterContextProvider>
          <FirebaseProvider>
            <AppContextProvider
              businessUnitSlug={businessUnitSlug}
              businessUnitConfig={businessUnitConfig}
              country={country}
              countryCode={countryCode}
              language={language}
              locale={locale}
              fullUrl={fullUrl}
              isMobile={isMobile}
              footer={footer}
              topbar={topbar}
              host={host}
            >
              <AttributionProvider>
                <Component {...pageProps} />
              </AttributionProvider>
            </AppContextProvider>
          </FirebaseProvider>
        </RouterContextProvider>
      </Container>
    );
  }
}

const BaseApp = withApolloClient(withIntlProvider(withMaterialUIProvider(MyApp)));

export { BaseApp as default };
